// SubscriptionFAQs.jsx
import React, { useState } from 'react';
import { X, Search, ChevronDown, ChevronUp } from 'lucide-react';

const SubscriptionFAQs = ({ isOpen, onClose }) => {
  const [activeSection, setActiveSection] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Toggle FAQ section
  const toggleSection = (sectionIndex) => {
    setActiveSection(activeSection === sectionIndex ? null : sectionIndex);
  };
  
  // FAQ data structure
  const faqSections = [
    {
      title: "Understanding Subscription Upgrades",
      faqs: [
        {
          question: "What happens when I upgrade my subscription?",
          answer: "When you upgrade, we upgrade your subscription to the new plan with higher-tier features. Your remaining value from your current plan is accounted for. For India users, we cancel your current subscription and create a new one automatically (applying a discount/refund to account for remaining value)"
        },
        {
          question: "Will I lose my remaining time when I upgrade?",
          answer: "No. We calculate both your remaining time and unused resources to determine the fair value of your current subscription."
        },
        {
          question: "When does my upgraded plan take effect?",
          answer: "For payments via Razorpay, your upgraded plan takes effect immediately. For PayPal, the timing depends on your upgrade type, but typically at the end of your current billing cycle for monthly subscriptions and immediately if upgrading from annual subscription"
        }
      ]
    },
    {
      title: "Payment Method Specific Approaches",
      faqs: [
        {
          question: "How does my upgrade work with UPI or card payments in India?",
          answer: "For UPI and card payments, you just have to click upgrade and we internally cancel and get you on to the upgraded plan immediately. Note that on the initial payment screen, you will see the full amount and something like \"1 offer selected\". When you go to the next screen, the discounted amount will be shown."
        },
        {
          question: "How does my upgrade work with India-based NetBanking and other payment methods?",
          answer: "For India-based NetBanking and other payment methods, you'll need to pay the full amount for the new subscription. We'll cancel your existing subscription and process a refund for its remaining value within 3-5 working days."
        },
        {
          question: "How do PayPal upgrades work?",
          answer: "For PayPal upgrades from monthly to monthly plans, we update your subscription to the new plan at the end of your billing cycle. For annual to annual upgrades, we calculate the proration amount, create a one-time payment link, and update your plan once the payment is completed."
        }
      ]
    },
    {
      title: "Discount & Proration Calculation",
      faqs: [
        {
          question: "How is my remaining subscription value calculated?",
          answer: "We calculate the remaining value based on the minimum of two factors: the percentage of time remaining in your billing cycle and the percentage of resources remaining. This ensures fair billing regardless of usage patterns."
        },
        {
          question: "Why is my discount based on the minimum of time and resources remaining?",
          answer: "This approach ensures fair billing. If you've used most of your resources but have significant time remaining, your discount is based on your remaining resources. Conversely, if you have many resources left but little time, your discount is based on the time remaining."
        }
      ]
    },
    {
      title: "Billing & Payments",
      faqs: [
        {
          question: "Will I be charged immediately when I upgrade?",
          answer: "For upgrades in India, yes, you'll be charged immediately for the new subscription (with any applicable discounts). For PayPal, it depends on your upgrade type - monthly to monthly upgrades are charged at the end of your billing cycle, while annual to annual upgrades require an immediate proration payment."
        },
        {
          question: "How do refunds work for NetBanking and other payment methods?",
          answer: "For NetBanking and other payment methods (excluding UPI and card payments), you'll pay the full amount for the new subscription, and we'll process a refund for the remaining value of your current subscription within 3-5 working days."
        },
        {
          question: "Can I get a discount instead of a refund for NetBanking payments?",
          answer: "Unfortunately, our payment gateway only supports offer-based discounts for UPI and card payments. For NetBanking and other payment methods, we have to process a refund instead."
        }
      ]
    },
    {
      title: "Resource Management During Upgrades",
      faqs: [
        {
          question: "What happens to my resources when I upgrade?",
          answer: "Your resources are immediately upgraded when your new subscription takes effect. In some cases, we may provide temporary additional resources during the transition period."
        },
        {
          question: "Will I have access to my new plan's features immediately?",
          answer: "Yes, for most payment methods your upgrade takes effect immediately, giving you instant access to the new features. For PayPal monthly plans, the upgrade occurs at the next billing cycle."
        },
        {
          question: "Will my data or settings be affected when I upgrade?",
          answer: "No. All your data, settings, and existing content will remain intact during the upgrade process."
        }
      ]
    },
    {
      title: "Special Cases & Limitations",
      faqs: [
        {
          question: "Can I downgrade my subscription?",
          answer: "Downgrades involve complex billing adjustments and require manual processing. Please contact our support team who will assist you with your downgrade request."
        },
        {
          question: "What if my upgrade payment fails?",
          answer: "If your payment fails, the state of your subscription will depend on the point of failure. You should contact support if you see an issue."
        }
      ]
    },
    {
      title: "Technical Support",
      faqs: [
        {
          question: "Who should I contact if I have questions about my upgrade?",
          answer: "For any questions about your subscription or the upgrade process, please contact our support team through the contact form at the bottom of the page."
        },
        {
          question: "What information should I provide when contacting support about an upgrade issue?",
          answer: "Please provide your account email, subscription ID, the plans you're upgrading from and to, and the payment method you're using. This will help us assist you more efficiently."
        },
        {
          question: "Can I cancel an upgrade after initiating it?",
          answer: "Since we cancel your old subscription immediately for most payment methods, you would need to contact support to revert to your original plan."
        }
      ]
    }
  ];
  
  // Filter FAQs based on search term
  const filteredFAQs = searchTerm.trim() === '' 
    ? faqSections 
    : faqSections.map(section => ({
        ...section,
        faqs: section.faqs.filter(faq => 
          faq.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
          faq.answer.toLowerCase().includes(searchTerm.toLowerCase())
        )
      })).filter(section => section.faqs.length > 0);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75 overflow-y-auto">
      <div className="relative bg-[#0c1526] rounded-xl w-full max-w-3xl mx-4 my-8 max-h-[90vh] overflow-y-auto shadow-xl border border-gray-700">
        {/* Header */}
        <div className="sticky top-0 z-10 flex justify-between items-center p-4 border-b border-gray-700 bg-[#0c1526]">
          <h2 className="text-xl font-bold text-white">
            Subscription Upgrade FAQs
          </h2>
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-[#273349] text-gray-400 hover:text-white transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>
        
        {/* Search */}
        <div className="p-4 bg-[#1e293b] border-b border-gray-700">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Search FAQs..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-[#0c1526] border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>
        </div>
        
        {/* FAQ Content */}
        <div className="p-4">
          {filteredFAQs.length > 0 ? (
            filteredFAQs.map((section, sectionIndex) => (
              <div key={sectionIndex} className="mb-4">
                <button 
                  onClick={() => toggleSection(sectionIndex)}
                  className="w-full flex justify-between items-center p-3 bg-[#1e293b] hover:bg-[#273349] rounded-lg text-left transition-colors"
                >
                  <h3 className="text-md font-semibold text-white">{section.title}</h3>
                  {activeSection === sectionIndex ? (
                    <ChevronUp className="h-5 w-5 text-gray-400" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-gray-400" />
                  )}
                </button>
                
                {activeSection === sectionIndex && (
                  <div className="mt-2 pl-2">
                    {section.faqs.map((faq, faqIndex) => (
                      <div key={faqIndex} className="mb-3 border-l-2 border-gray-700 pl-3">
                        <h4 className="text-white font-medium mb-1">{faq.question}</h4>
                        <p className="text-gray-300 text-sm">{faq.answer}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))
          ) : (
            <div className="text-center py-8">
              <p className="text-gray-400">No FAQs match your search.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SubscriptionFAQs;